import { z } from 'zod';
import type { SoftwareFamily } from './domain.js';

export const BedrockPortSchema = z.number().int().min(19132).max(19231);
export const BedrockSettingsSchema = z.object({ enabled: z.boolean(), port: BedrockPortSchema }).strict();
export type BedrockSettings = z.infer<typeof BedrockSettingsSchema>;
export const BedrockUrlSchema = z
  .string()
  .url()
  .refine((text) => {
    const url = URL.parse(text);
    if (!url) return false;
    return (
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      !url.port &&
      ['cdn.modrinth.com', 'download.geysermc.org'].includes(url.hostname)
    );
  }, 'Bedrock download host is not allowed');
export const BedrockFileSchema = z
  .object({
    name: z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9._+-]*\.jar$/),
    url: BedrockUrlSchema,
    sha256: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .optional(),
    sha512: z
      .string()
      .regex(/^[0-9a-f]{128}$/)
      .optional(),
    size: z.number().int().positive(),
    target: z.enum(['plugins', 'mods']),
    project: z.enum(['geyser', 'floodgate']),
    version: z.string().min(1),
  })
  .strict()
  .refine((file) => Boolean(file.sha256 || file.sha512), 'Checksum is required');
export type BedrockFile = z.infer<typeof BedrockFileSchema>;
export const BedrockDeploymentSchema = z
  .object({
    proxied: z.boolean().optional(),
    settings: BedrockSettingsSchema,
    files: z.array(BedrockFileSchema).max(2),
  })
  .strict();
export type BedrockDeployment = z.infer<typeof BedrockDeploymentSchema>;
export const BedrockViewSchema = BedrockDeploymentSchema.extend({
  requiresMigration: z.boolean().optional(),
  support: z.enum(['plugin', 'mod', 'needs-paper', 'unsupported']),
  host: z.string(),
  address: z.string(),
});
export type BedrockView = z.infer<typeof BedrockViewSchema>;

export function bedrockSupport(family: SoftwareFamily): BedrockView['support'] {
  if (family === 'paper' || family === 'purpur') return 'plugin';
  if (family === 'fabric') return 'mod';
  return family === 'vanilla' ? 'needs-paper' : 'unsupported';
}

export function allocateBedrockPort(used: number[]): number {
  const ports = new Set(used);
  for (let port = 19132; port <= 19231; port++) if (!ports.has(port)) return port;
  throw new Error('統合版の空きポートがありません');
}

/**
 * Geyser \u306E\u8A2D\u5B9A\u3092\u3001\u7D71\u5408\u7248\u306E\u30DD\u30FC\u30C8\u3068 floodgate \u8A8D\u8A3C\u306B\u3059\u308B\u3002
 * Floodgate \u304C\u5165\u3063\u3066\u3044\u3066\u3082 Geyser \u306F\u8A8D\u8A3C\u65B9\u5F0F\u3092\u81EA\u52D5\u3067\u306F\u5207\u308A\u66FF\u3048\u306A\u3044\uFF082026-10-10 \u306B\u5B9F\u6A5F\u3067\u78BA\u8A8D\uFF09\u306E\u3067\u3001
 * auth-type \u3092\u5FC5\u305A floodgate \u306B\u3059\u308B\u3002\u53E4\u3044\u5F62\u5F0F\uFF08remote: \u306E\u4E2D\u306B auth-type\uFF09\u306A\u3089\u305D\u3061\u3089\u3092\u66F8\u304D\u63DB\u3048\u308B\u3002
 */
export function setGeyserConfig(yamlText: string | null, port: number): string {
  const withPort = setGeyserPort(yamlText, port);
  const block = blockLines(withPort, 'remote');
  const legacy = block && block.some((line) => /^ +auth-type:/.test(line));
  return setBlockKey(withPort, legacy ? 'remote' : 'java', 'auth-type', 'floodgate');
}

/** Block YAML only: retain every unrelated byte, including comments and newline style. */
export function setGeyserPort(yamlText: string | null, port: number): string {
  BedrockPortSchema.parse(port);
  return setBlockKey(yamlText ?? '', 'bedrock', 'port', String(port));
}

function splitLines(text: string) {
  return text.match(/[^\r\n]*(?:\r\n|\n|\r|$)/g)?.filter(Boolean) ?? [];
}

/** \u30C8\u30C3\u30D7\u30EC\u30D9\u30EB\u306E\u30D6\u30ED\u30C3\u30AF\uFF08`name:`\uFF09\u306E\u4E2D\u306E\u884C\u3002\u30D6\u30ED\u30C3\u30AF\u304C\u7121\u3051\u308C\u3070 null */
function blockLines(text: string, name: string): string[] | null {
  const lines = splitLines(text.replace(/^\uFEFF/, ''));
  const header = new RegExp(`^${name}:\\s*(?:#.*)?(?:\\r\\n|\\n|\\r)?$`);
  const start = lines.findIndex((line) => header.test(line));
  if (start < 0) return null;
  let end = start + 1;
  while (end < lines.length && !/^[^\s#]/.test(lines[end]!)) end++;
  return lines.slice(start + 1, end);
}

/** \u30C8\u30C3\u30D7\u30EC\u30D9\u30EB\u306E\u30D6\u30ED\u30C3\u30AF `block:` \u306E\u76F4\u4E0B\u306E `key:` \u3092 value \u306B\u3059\u308B\uFF08\u7121\u3051\u308C\u3070\u8DB3\u3059\uFF09 */
function setBlockKey(input: string, block: string, key: string, value: string): string {
  const text = input;
  if (text.startsWith('\uFEFF')) return `\uFEFF${setBlockKey(text.slice(1), block, key, value)}`;
  const newline = /\r\n|\n|\r/.exec(text)?.[0] ?? '\n';
  const lines = splitLines(text);
  const header = new RegExp(`^${block}:\\s*(?:#.*)?(?:\\r\\n|\\n|\\r)?$`);
  const headers = lines.filter((line) => new RegExp(`^${block}:`).test(line));
  if (headers.length > 1 || headers.some((line) => !header.test(line)))
    throw new Error(`Unsupported ${block} YAML block`);
  const start = lines.findIndex((line) => header.test(line));
  if (start < 0)
    return `${text}${text && !/[\r\n]$/.test(text) ? newline : ''}${block}:${newline}  ${key}: ${value}${newline}`;
  let end = start + 1;
  while (end < lines.length && !/^[^\s#]/.test(lines[end]!)) end++;
  const indent = lines
    .slice(start + 1, end)
    .map((line) => /^( +)[^#\s]/.exec(line)?.[1]?.length)
    .filter((length): length is number => length !== undefined);
  const width = indent.length ? Math.min(...indent) : 2;
  const pattern = new RegExp(`^( {${width}}${key}:[ \\t]*)([^#\\r\\n]*?)([ \\t]*(?:#.*)?)(\\r\\n|\\n|\\r)?$`);
  const found = lines
    .slice(start + 1, end)
    .map((line, index) => (pattern.test(line) ? start + 1 + index : -1))
    .filter((index) => index >= 0);
  if (found.length > 1) throw new Error(`Duplicate ${block} ${key}`);
  if (found.length) {
    const index = found[0]!;
    lines[index] = lines[index]!.replace(
      pattern,
      (_match, prefix: string, _old: string, suffix: string, eol: string | undefined) =>
        `${prefix}${prefix.endsWith(':') ? ' ' : ''}${value}${suffix}${eol ?? ''}`,
    );
  } else {
    if (!/[\r\n]$/.test(lines[start]!)) lines[start] += newline;
    lines.splice(start + 1, 0, `${' '.repeat(width)}${key}: ${value}${newline}`);
  }
  return lines.join('');
}
